import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import axios, { type AxiosInstance, AxiosError } from 'axios';
import { API_HOST } from '@/constants/api-routes';
import type { AxiosBaseQueryArgs } from '@/types/api.types';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_HOST,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * A wrapper function for making HTTP requests using Axios.
 * It handles sending the request with the given method and data,
 * and returns the response data or an error object if the request fails.
 * @param {AxiosBaseQueryArgs} args - An object containing the URL, method, data, and params for the request.
 * @returns {Promise<Object>} A promise resolving to an object with either the response data or an error object.
 */
export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs> =>
  async ({ url, method, data, params }) => {
    try {
      const headers =
        data instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : { 'Content-Type': 'application/json' };

      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
      });

      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      return {
        error: {
          status: err.response?.status || 500,
          data: err.response?.data || err.message || 'Unknown error',
        },
      };
    }
  };
